// Verlauf der Gespräche (T48, „Conversations > History“): Gesprächskarten nach Tagen mit Notiz und Player, Suche in Notizen, Filter
// nach Mitarbeiter, Lead, Zeitraum, Dauer, Richtung, Ergebnis und „nur aufgenommene“. Der Zustand steht in der Adresse (teilbar).
// Wer nicht alle Gespräche sehen darf, sieht die eigenen.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { gespraecheApi, type Gespraech } from "../api/gespraeche";
import { useEchtzeit } from "../api/echtzeit";
import { Laden, Leer } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { Icon } from "../components/Icon";
import { useMeldung } from "../components/Meldungen";
import { useMeta } from "../daten/meta";
import { useSeitenKopf } from "../daten/seite";
import { useListe } from "../kontakte/liste/useListe";
import { heuteTag } from "../aktivitaeten/zeiten";
import { T } from "../texte";
import { kopieren } from "../werkzeuge/tasten";
import { berlinDatum, tagText } from "../werkzeuge/zeit";
import { GespraechKarte } from "./GespraechKarte";
import { GespraecheKopf, useDarfAlle } from "./GespraecheKopf";
import { apiFilter, filterAusParams, filterGesetzt, paramAenderung, suchbegriffe, type VerlaufFilter } from "./logik";
import { VerlaufFilterZeile } from "./VerlaufFilter";
import "./gespraeche.css";

type Zeile = Gespraech & { id: string };
const SEITE = 30;

export function Verlauf() {
  useSeitenKopf([{ label: T.nav.gespraeche }]);
  const meta = useMeta();
  const meldung = useMeldung();
  const darfAlle = useDarfAlle();
  const [params, setParams] = useSearchParams();
  const filter = useMemo(() => filterAusParams(params), [params]);
  const [sucheEingabe, setSucheEingabe] = useState(filter.q);
  const [neu, setNeu] = useState(false);

  const setParam = useCallback(
    (aenderungen: Record<string, string | null>) => {
      setParams(
        (alt) => {
          const n = new URLSearchParams(alt);
          for (const [k, v] of Object.entries(aenderungen)) {
            if (v) n.set(k, v);
            else n.delete(k);
          }
          return n;
        },
        { replace: true },
      );
    },
    [setParams],
  );
  const aendern = useCallback((teil: Partial<VerlaufFilter>) => setParam(paramAenderung(teil)), [setParam]);

  // Suchtext erst nach einer kurzen Pause in die Adresse schreiben
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (sucheEingabe.trim() !== filter.q.trim()) aendern({ q: sucheEingabe });
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sucheEingabe]);
  useEffect(() => {
    setSucheEingabe((alt) => (alt.trim() === filter.q.trim() ? alt : filter.q));
  }, [filter.q]);

  const abfrage = useMemo(() => apiFilter(filter, heuteTag()), [filter]);
  const schluessel = JSON.stringify(abfrage);
  const holen = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      gespraecheApi.history({ ...abfrage, cursor, limit: SEITE }, signal).then((r) => ({ ...r, data: r.data.map((g): Zeile => ({ ...g, id: g.call_id })) })),
    // `schluessel` fasst alle Eingaben zusammen
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schluessel],
  );
  const liste = useListe<Zeile>(holen, schluessel);
  const { zeilen } = liste;
  useEffect(() => setNeu(false), [schluessel]);

  // Neue Gespräche melden, nicht einfach nachladen (die Liste soll beim Lesen nicht springen)
  useEchtzeit(["activity.created", "activity.updated", "call.ended"], (e) => {
    if (e.type === "call.ended") return setNeu(true);
    const typ = String((e.data as { activity_type?: string }).activity_type ?? "");
    if (!typ || typ === "Call") setNeu(true);
  });

  // Weiter laden, sobald das Listenende ins Bild kommt
  const ende = useRef<HTMLDivElement>(null);
  const mehrRef = useRef(liste.mehr);
  mehrRef.current = liste.mehr;
  useEffect(() => {
    const el = ende.current;
    if (!el || !liste.hatMehr) return;
    const beo = new IntersectionObserver((eintraege) => eintraege.some((x) => x.isIntersecting) && mehrRef.current(), { rootMargin: "400px" });
    beo.observe(el);
    return () => beo.disconnect();
  }, [liste.hatMehr, zeilen.length]);

  const begriffe = useMemo(() => suchbegriffe(filter.q), [filter.q]);
  const nachTag = filter.sort !== "lang";
  const leeren = () => {
    setSucheEingabe("");
    setParam({ q: null, match: null, user: null, lead: null, leadName: null, zeit: null, von: null, bis: null, richtung: null, min: null, aufgenommen: null, ergebnis: null, alle: null });
  };
  const linkKopieren = () =>
    void kopieren(window.location.href).then((ok) => meldung.zeige(ok ? "ok" : "fehler", ok ? "Link kopiert." : "Kopieren hat nicht geklappt."));

  let letzterTag = "";
  return (
    <div className="gesp">
      <GespraecheKopf />
      <VerlaufFilterZeile
        filter={filter}
        suche={sucheEingabe}
        onSuche={setSucheEingabe}
        onAendern={aendern}
        onLeeren={leeren}
        nutzer={meta.nutzerListe}
        alle={darfAlle}
        total={liste.total}
        onLink={linkKopieren}
      />
      <div className="gesp-verlauf">
        {neu ? (
          <div style={{ marginBottom: "var(--space-2)" }}>
            <button type="button" className="knopf knopf--klein gesp-filter__neu" onClick={() => (setNeu(false), liste.neuLaden(true))}>
              <Icon name="neu-laden" groesse={13} />
              Neue Gespräche anzeigen
            </button>
          </div>
        ) : null}
        {liste.fehler ? <FehlerBox fehler={liste.fehler} onNochmal={() => liste.neuLaden()} /> : null}
        {liste.laedt && !zeilen.length ? <Laden /> : null}
        {!liste.laedt && !liste.fehler && !zeilen.length ? (
          <div className="gesp-leer">
            <Leer
              titel={filterGesetzt(filter) ? "Kein Gespräch gefunden." : "Noch keine Gespräche."}
              text={
                filterGesetzt(filter)
                  ? "Suche oder Filter anpassen. Anrufe ohne Dauer und Notiz zeigt „Weitere Filter“ unter „Auch Anrufe ohne Gespräch“."
                  : darfAlle
                    ? "Sobald jemand telefoniert, erscheint das Gespräch hier."
                    : "Hier erscheinen deine Gespräche, sobald du telefoniert hast."
              }
            />
          </div>
        ) : null}
        {zeilen.map((g) => {
          const tag = nachTag ? berlinDatum(g.zeit) : "";
          const kopf = tag && tag !== letzterTag;
          letzterTag = tag || letzterTag;
          return (
            <Fragment key={g.id}>
              {kopf ? <div className="gesp-tag">{tagText(tag)}</div> : null}
              <GespraechKarte gespraech={g} begriffe={begriffe} mitMitarbeiter={darfAlle} />
            </Fragment>
          );
        })}
        {liste.hatMehr ? (
          <>
            <div ref={ende} className="gesp-sentinel" />
            <div className="gesp-mehr">
              <button type="button" className="knopf" disabled={liste.laedtMehr} onClick={liste.mehr}>
                {liste.laedtMehr ? "Lädt …" : "Weitere laden"}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
